import React, { useMemo } from "react";
import { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";

import { baseDrawControlModes } from "fond/draw/helper";
import { onAfterDragAerial } from "fond/draw/modes/planner/aerial/onAfterDragAerial";
import { onTrashAerial } from "fond/draw/modes/planner/aerial/onTrashAerial";
import { onAfterDragUnderground } from "fond/draw/modes/planner/underground/onAfterDragUnderground";
import { onCreateUndergroundPath } from "fond/draw/modes/planner/underground/onCreateUndergroundPath";
import { onCreateUndergroundPoint } from "fond/draw/modes/planner/underground/onCreateUndergroundPoint";
import { onSplitUndergroundPath } from "fond/draw/modes/planner/underground/onSplitUndergroundPath";
import { onTrashUnderground } from "fond/draw/modes/planner/underground/onTrashUnderground";
import MultiLayerEditPanelContent from "fond/draw/MultiLayerEdit/MultiLayerEditPanelContent";
import { MultiLayerEditLegacyProvider } from "fond/draw/MultiLayerEdit/MultiLayerEditProviderLegacy";
import { makeValidateAerialPole, makeValidateLeadIn, makeValidateUgPath, makeValidateUgPoint } from "fond/draw/validation";
import {
  DeleteButton,
  JoinButton,
  LineStringButton,
  PointButton,
  PolygonSelectButton,
  RedoButton,
  SelectButton,
  SnappingButton,
  SplitButton,
  ToolbarConfig,
  TopologicalButton,
  UndoButton,
} from "fond/map/Toolbar";
import { getCurrentProjectData } from "fond/project";
import { featureCollection } from "fond/turf";
import { EditingLayer, LayerId, LayerIds } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";

import { compositeDrawLineString, compositeDrawPoint } from "./modes";

const allToolConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [
      <SelectButton key="select" />,
      <PolygonSelectButton key="selectPolygon" />,
      <PolygonSelectButton key="selectLasso" selectionMode="lasso" />,
    ],
  },
  {
    id: "drawTools",
    type: "mode",
    items: [<PointButton key="drawPoint" />, <LineStringButton key="drawLine" />],
  },
  {
    id: "modifyTools",
    type: "action",
    items: [
      <SplitButton key="split" />,
      <JoinButton key="join" />,
      // <UnionButton key="union" />,
    ],
  },
  {
    id: "miscTools",
    type: "action",
    items: [<SnappingButton key="snapping" />, <TopologicalButton key="topological" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

// Create custom validation functions for ExtendedDesign layers
const validateLeadIn = makeValidateLeadIn({
  demandLayerId: LayerIds.inAddress,
  ugPointsLayerId: LayerIds.inStreetPoints,
  aerialPoleLayerId: LayerIds.inPole,
});

const validateUgPoint = makeValidateUgPoint({
  ugPathLayerId: LayerIds.inStreet,
  leadinsLayerId: LayerIds.inLeadIns,
});

const validateUgPath = makeValidateUgPath({
  ugPointsLayerId: LayerIds.inStreetPoints,
  aerialPoleLayerId: LayerIds.inPole,
});

const validateAerialPole = makeValidateAerialPole({
  spanLayerId: LayerIds.inSpan,
});

const editingLayers: EditingLayer[] = [
  {
    ID: LayerIds.inAddress,
    GeometryType: "Point",
    getFeatureProperties: () => ({ layerId: LayerIds.inAddress }),
  },
  {
    ID: LayerIds.inLeadIns,
    GeometryType: "LineString",
    validate: validateLeadIn,
    disabledTools: ["split", "join"],
    options: {
      // Leadins are always a single segment between a demand point and the network.
      midPoints: false,
      maxVertices: 2,
    },
    getFeatureProperties: () => ({ layerId: LayerIds.inLeadIns }),
  },
  {
    ID: LayerIds.inStreetPoints,
    GeometryType: "Point",
    validate: validateUgPoint,
    onCreate: onCreateUndergroundPoint,
    onAfterDrag: onAfterDragUnderground,
    onTrash: onTrashUnderground,
  },
  {
    ID: LayerIds.inStreet,
    GeometryType: "LineString",
    validate: validateUgPath,
    onCreate: onCreateUndergroundPath,
    onSplit: onSplitUndergroundPath,
    onAfterDrag: onAfterDragUnderground,
    onTrash: onTrashUnderground,
    // No getFeatureProperties here — onCreateUndergroundPath sets layerId
    // on each feature directly. Using getFeatureProperties would cause
    // handleOnCreate to overwrite ALL features (including auto-created
    // points) with { layerId: inStreet }, breaking point styling.
  },
  {
    ID: LayerIds.inSpan,
    GeometryType: "LineString",
    disabledTools: ["split", "join"],
    options: { midPoints: false },
    // The span layer enforces topological editing with the pole layer.
    compulsoryTopologicalLayerIds: [LayerIds.inPole, LayerIds.inSpan],
    // The span layer has custom onAfterDrag and onTrash handlers to manage pole merging, span splitting, and snapped status.
    onAfterDrag: onAfterDragAerial,
    onTrash: onTrashAerial,
  },
  {
    ID: LayerIds.inPole,
    GeometryType: "Point",
    validate: validateAerialPole,
    // The pole layer enforces topological editing with the span layer.
    compulsoryTopologicalLayerIds: [LayerIds.inPole, LayerIds.inSpan],
    // The pole layer has custom onAfterDrag and onTrash handlers to manage pole merging, span splitting, and snapped status.
    onAfterDrag: onAfterDragAerial,
    onTrash: onTrashAerial,
  },
];

const baseOptions: MapboxDrawOptions = {
  snap: true,
  topologicalEditing: true,
  modes: {
    ...baseDrawControlModes,
    draw_point: compositeDrawPoint,
    draw_line_string: compositeDrawLineString,
  },
};

interface ModificationsDrawProps {
  /**
   * The layers initially editable, the first being initially selected.
   * Defaults to all layers, with leadins selected.
   */
  layerIds?: LayerId[];
  title?: string;
}

/**
 * Multi-layer draw screen for planner extended data prep layers.
 */
const ModificationsDraw: React.FC<ModificationsDrawProps> = ({ layerIds, title }: ModificationsDrawProps) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const projectData = useAppSelector((state) => getCurrentProjectData(state.project));

  const options: MapboxDrawOptions = useMemo(
    () => ({
      ...baseOptions,
      editOptions: {
        allLayers: editingLayers,
        editingLayerId: layerIds?.[0] ?? LayerIds.inLeadIns,
        editingLayerIds: layerIds,
      },
    }),
    [layerIds]
  );

  // Get features from Redux (same source as MapContent) to ensure IDs match what's on the map
  const initialFeatures = useMemo(() => {
    const features = editingLayers.flatMap(({ ID }) => projectData?.layers?.[ID]?.features ?? []);
    return features.length > 0 ? featureCollection(features) : undefined;
  }, [projectData]);

  return (
    <MultiLayerEditLegacyProvider versionId={versionId} options={options} config={allToolConfig} initialFeatures={initialFeatures}>
      <MultiLayerEditPanelContent title={title} />
    </MultiLayerEditLegacyProvider>
  );
};

export default ModificationsDraw;
